<template>
  <Transition name="count-toast">
    <button
      v-if="notice"
      type="button"
      class="count-toast"
      :class="notice.tone"
      :aria-live="notice.tone === 'error' ? 'assertive' : 'polite'"
      @click="onClick"
    >
      <span class="title">{{ notice.title }}</span>
      <span class="body">{{ notice.body }}</span>
    </button>
  </Transition>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { TOURNAMENT_JOIN_CUTOFF_MS } from '@shared/index'
import { useI18n } from '../i18n/t'
import { armTournamentPush, askTournamentPush, tournamentNoticesAllowed } from '../push/arm'
import { tournamentPushOn } from '../push/device'
import { useTournamentStore } from '../stores/tournament'

const props = defineProps<{
  /** Intro is up. Keep checking, and show the notice once it closes. */
  hold: boolean
}>()

type MarkId = '2h' | '1h' | '5m' | '1m'
type Tone = 'warn' | 'error'

/**
 * Fire once while the clock is inside the window, not if the player
 * arrives long after that mark.
 */
const MARKS: { id: MarkId; at: number; slack: number; tone: Tone }[] = [
  { id: '2h', at: 2 * 60 * 60 * 1000, slack: 2 * 60 * 1000, tone: 'warn' },
  { id: '1h', at: 60 * 60 * 1000, slack: 2 * 60 * 1000, tone: 'warn' },
  { id: '5m', at: 5 * 60 * 1000, slack: 90 * 1000, tone: 'warn' },
  { id: '1m', at: 60 * 1000, slack: 50 * 1000, tone: 'error' },
]

const SEEN_KEY = 'dc-tournament-countdown'
const STAY_MS: Record<MarkId, number> = {
  '2h': 10_000,
  '1h': 10_000,
  '5m': 14_000,
  '1m': 0,
}

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const tournament = useTournamentStore()

const notice = ref<{
  id: MarkId
  tone: Tone
  title: string
  body: string
  start: string
} | null>(null)

let timer = 0
let hideTimer = 0

function loadSeen(start: string): Set<string> {
  try {
    const raw = sessionStorage.getItem(SEEN_KEY)
    if (!raw) return new Set()
    const parsed = JSON.parse(raw) as { start?: string; marks?: string[] }
    if (parsed.start !== start || !Array.isArray(parsed.marks)) return new Set()
    return new Set(parsed.marks)
  } catch {
    return new Set()
  }
}

function saveSeen(start: string, marks: Set<string>): void {
  try {
    sessionStorage.setItem(
      SEEN_KEY,
      JSON.stringify({ start, marks: [...marks] }),
    )
  } catch {
    /* private mode still keeps the notice on screen */
  }
}

function copy(id: MarkId): { title: string; body: string } {
  return {
    title: t(`tournament.notice${id}Title`),
    body: t(`tournament.notice${id}Body`),
  }
}

function dismiss(): void {
  window.clearTimeout(hideTimer)
  notice.value = null
}

function show(id: MarkId, tone: Tone, start: string): void {
  const text = copy(id)
  notice.value = { id, tone, start, ...text }
  window.clearTimeout(hideTimer)
  const stay = STAY_MS[id]
  if (stay > 0) hideTimer = window.setTimeout(dismiss, stay)
  armTournamentPush()
  if (tournamentPushOn()) return
  if (!tournamentNoticesAllowed()) return
  if (document.visibilityState === 'visible') return
  try {
    const note = new Notification(text.title, { body: text.body })
    note.onclick = () => {
      window.focus()
      note.close()
    }
  } catch {
    /* the banner is there when the tab is visible again */
  }
}

function tick(): void {
  const row = tournament.current
  if (!row || row.sample || row.status === 'live') {
    dismiss()
    return
  }
  if (row.autosPaused && row.status === 'upcoming') {
    dismiss()
    return
  }
  const startMs = Date.parse(row.startsAt)
  if (!Number.isFinite(startMs)) return
  const left = startMs - Date.now()
  if (left <= 0) {
    dismiss()
    return
  }
  if (notice.value && notice.value.start !== row.startsAt) dismiss()
  if (props.hold) return
  const seen = loadSeen(row.startsAt)
  for (const mark of MARKS) {
    if (seen.has(mark.id)) continue
    if (left > mark.at || left <= mark.at - mark.slack) continue
    seen.add(mark.id)
    saveSeen(row.startsAt, seen)
    show(mark.id, mark.tone, row.startsAt)
    return
  }
}

function onClick(): void {
  void askTournamentPush()
  const row = tournament.current
  const current = notice.value
  dismiss()
  if (!row || !current) return
  if (current.id !== '5m' && current.id !== '1m') return
  if (row.sample || row.status !== 'registration' || tournament.joined) return
  const start = Date.parse(row.startsAt)
  if (!Number.isFinite(start) || start - Date.now() <= TOURNAMENT_JOIN_CUTOFF_MS) return
  const name = String(route.name || '')
  if (name !== 'home' && name !== 'lobby' && name !== 'tournaments') {
    void router.push({ name: 'home' }).then(() => tournament.openPicker())
    return
  }
  tournament.openPicker()
}

watch(
  () => [tournament.current?.startsAt, tournament.current?.status, props.hold] as const,
  () => tick(),
)

onMounted(() => {
  tick()
  timer = window.setInterval(tick, 5_000)
  document.addEventListener('visibilitychange', tick)
})

onUnmounted(() => {
  window.clearInterval(timer)
  window.clearTimeout(hideTimer)
  document.removeEventListener('visibilitychange', tick)
})
</script>

<style scoped>
.count-toast {
  position: fixed;
  left: 50%;
  top: calc(var(--app-nav-offset, 64px) + 16px);
  z-index: 85;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  width: min(720px, calc(100vw - 48px));
  margin: 0;
  padding: 20px 32px;
  border: 0;
  border-radius: 16px;
  font: inherit;
  text-align: center;
  cursor: pointer;
  transform: translateX(-50%);
  box-shadow: var(--md-elevation-2);
}

.count-toast.warn {
  background: #f5c518;
  color: #1c1400;
}

.count-toast.error {
  background: #ba1a1a;
  color: #ffffff;
}

.title {
  font-size: 26px;
  font-weight: 700;
  line-height: 1.2;
}

.body {
  font-size: 16px;
  font-weight: 500;
  line-height: 1.35;
}

.count-toast-enter-active,
.count-toast-leave-active {
  transition:
    opacity var(--md-motion-short) var(--md-motion-standard),
    transform var(--md-motion-short) var(--md-motion-standard);
}

.count-toast-enter-from,
.count-toast-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-12px);
}

@media (max-width: 720px) {
  .count-toast {
    top: auto;
    bottom: calc(88px + env(safe-area-inset-bottom, 0px));
    width: min(440px, calc(100vw - 24px));
    padding: 16px 20px;
  }

  .title {
    font-size: 20px;
  }

  .body {
    font-size: 15px;
  }

  .count-toast-enter-from,
  .count-toast-leave-to {
    transform: translateX(-50%) translateY(12px);
  }
}

@media (prefers-reduced-motion: reduce) {
  .count-toast-enter-active,
  .count-toast-leave-active {
    transition: none;
  }
}
</style>
